import React, { useState, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { T } from "../design/tokens";
import { CATALOGUE, FEATURED_ID } from "../data/catalogue";
import { db } from "../supabaseClient";

export default function TabRaceBook({ races, profile, onUpdateRace, addRace, session }) {
  const [view, setView]             = useState("catalogue");
  const [selectedId, setSelectedId] = useState(null);
  const [activeTag, setActiveTag]   = useState("Tout");
  const [sortBy, setSortBy]         = useState("DATE");
  const [stickyDismissed, setStickyDismissed] = useState(false);
  const [showContribForm, setShowContribForm] = useState(false);
  const [contribForm, setContribForm] = useState({ badge: "TIP", km: "", texte: "", edition: "" });
  const [contribSubmitting, setContribSubmitting] = useState(false);
  const [contribOk, setContribOk]   = useState(false);
  // Loaded contributions from Supabase keyed by race id
  const [liveContribs, setLiveContribs] = useState({});

  // Load contributions when opening a book
  useEffect(() => {
    if (!selectedId || !db) return;
    db.getContributions(selectedId).then(rows => {
      if (rows?.length) setLiveContribs(prev => ({ ...prev, [selectedId]: rows }));
    });
  }, [selectedId]);

  const selectedBook = CATALOGUE.find(b => b.id === selectedId);
  const featured = CATALOGUE.find(b => b.id === FEATURED_ID);

  const openBook = (id) => { setSelectedId(id); setView("book"); setStickyDismissed(false); window.scrollTo(0,0); };
  const backToCatalogue = () => { setView("catalogue"); setSelectedId(null); };

  // Filter + sort
  const ALL_TAGS = ["Tout", ...Array.from(new Set(CATALOGUE.flatMap(b => b.tags)))];
  const filtered = CATALOGUE
    .filter(b => activeTag === "Tout" || b.tags.includes(activeTag))
    .sort((a, b) => {
      if (sortBy === "DISTANCE") return b.distance - a.distance;
      if (sortBy === "D+") return b.dplus - a.dplus;
      if (sortBy === "DIFFICULTÉ") return b.itra - a.itra;
      return a.dateISO > b.dateISO ? 1 : -1; // DATE
    });
  const tagCount = (tag) => tag === "Tout" ? CATALOGUE.length : CATALOGUE.filter(b => b.tags.includes(tag)).length;

  // Merge static catalogue contributions with live DB contributions for a book
  const getContribs = (book) => {
    const live = liveContribs[book.id] || [];
    // Map live DB rows to same shape as static contributions
    const mapped = live.map(r => ({
      id: r.id, auteur: r.auteur || "Anonyme", numero: "—",
      edition: r.edition || "", temps: r.temps || "", badge: r.badge || "TIP",
      km: r.km || "", texte: r.texte, votes: r.votes || 0,
    }));
    return [...mapped, ...(book.contributions || [])];
  };

  // Elevation chart data helper
  const profileData = (book) => book.profile.map(([km, alt]) => ({ km, alt }));

  // Mini elevation chart (for catalogue cards)
  const MiniChart = ({ book, height = 60, fill = "#C49A3C20", stroke = "#1A1510" }) => (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={profileData(book)} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`grad-${book.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#C49A3C" stopOpacity={0.25} />
            <stop offset="95%" stopColor="#C49A3C" stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="alt" stroke={stroke} strokeWidth={1.5} fill={`url(#grad-${book.id})`} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );

  // ── CATALOGUE VIEW ────────────────────────────────────────────────────────
  if (view === "catalogue") {
    return (
      <div style={{ fontFamily: T.fontUI, color: T.text }}>

        {/* ── Header ── */}
        <div style={{ paddingBottom: 32, borderBottom: `0.5px solid ${T.border}` }}>
          <div style={{ fontSize: 72, lineHeight: 1, marginBottom: 16 }}>
            <span style={{ fontWeight: 900, color: T.navy }}>Race </span>
            <span style={{ fontWeight: 300, fontStyle: "italic", color: T.textMid }}>books.</span>
          </div>
          <div style={{ fontSize: 14, color: T.textMid, maxWidth: 480, lineHeight: 1.7 }}>
            Le catalogue éditorial des courses françaises. Chaque dossier est rédigé avec les finishers, mis à jour à chaque édition.
          </div>
        </div>

        {/* ── Filters + Sort ── */}
        <div style={{ padding: "16px 0", borderBottom: `0.5px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {ALL_TAGS.map(tag => (
              <button key={tag} onClick={() => setActiveTag(tag)} style={{
                padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                cursor: "pointer", fontFamily: T.fontUI, transition: "all 0.15s",
                background: activeTag === tag ? T.navy : "transparent",
                color: activeTag === tag ? "#fff" : T.textMid,
                border: `0.5px solid ${activeTag === tag ? T.navy : T.border}`,
              }}>
                {tag} {tagCount(tag) < CATALOGUE.length ? `0${tagCount(tag)}`.slice(-2) : `0${CATALOGUE.length}`.slice(-2)}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, fontSize: 11 }}>
            <span style={{ fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: T.textLight }}>TRIER</span>
            {["DATE", "DISTANCE", "D+", "DIFFICULTÉ"].map(s => (
              <button key={s} onClick={() => setSortBy(s)} style={{
                background: "none", border: "none", cursor: "pointer", fontFamily: T.fontUI,
                fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                color: sortBy === s ? T.navy : T.textMid,
                borderBottom: sortBy === s ? `2px solid ${T.navy}` : "2px solid transparent",
                padding: "2px 0",
              }}>{s}</button>
            ))}
          </div>
        </div>

        {/* ── Full grid ── */}
        <div style={{ paddingTop: 24, marginBottom: 40 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {filtered.map(book => (
              <div key={book.id} onClick={() => openBook(book.id)} style={{ borderRadius: 14, overflow: "hidden", boxShadow: T.shadow, cursor: "pointer", background: T.surface }}>
                {/* Colored top */}
                <div style={{ background: book.cardColor, padding: "20px 24px", minHeight: 180 }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
                    <span style={{ fontSize: 9, color: book.cardTextColor, opacity: 0.5, letterSpacing: 1 }}>{book.edition}</span>
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: book.cardTextColor, opacity: 0.65, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>
                    {book.tags.slice(0, 2).join(" · ")}
                  </div>
                  <div style={{ fontSize: 30, fontWeight: 900, color: book.cardTextColor, lineHeight: 1.1, marginBottom: 8 }}>{book.nom}</div>
                  <div style={{ fontSize: 12, color: book.cardTextColor, opacity: 0.75, lineHeight: 1.5 }}>{book.sousTitre}</div>
                </div>
                {/* White bottom */}
                <div style={{ padding: "16px 20px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
                    {[
                      { l: "DATE", v: book.date },
                      { l: "DIST.", v: `${book.distance}`, u: "km" },
                      { l: "D+", v: `${(book.dplus/1000).toFixed(0)}`, u: "k" },
                      { l: "ITRA", v: `${book.itra}★` },
                    ].map(s => (
                      <div key={s.l}>
                        <div style={{ fontSize: 9, color: T.textMid, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600, marginBottom: 2 }}>{s.l}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.navy, fontFamily: T.fontData }}>
                          {s.v}{s.u && <span style={{ fontSize: 10, color: T.textMid }}>{s.u}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <MiniChart book={book} height={50} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                    <span style={{ fontSize: 11, color: T.textMid }}>{book.region}</span>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: T.navy }}>→</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── BOOK DETAIL VIEW ─────────────────────────────────────────────────────
  if (view === "book" && selectedBook) {
    const book = selectedBook;
    const chartData = profileData(book);
    const BADGE_COLORS = { TIP: T.green, WARNING: T.orange, NUTRI: T.gold };

    return (
      <div style={{ fontFamily: T.fontUI, color: T.text, paddingBottom: 100 }}>

        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: T.textMid, marginBottom: 24 }}>
          <button onClick={backToCatalogue} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMid, fontFamily: T.fontUI, fontSize: 11, fontWeight: 700, padding: 0 }}>
            RACE BOOKS
          </button>
          <span>/</span>
          <span>N° {book.numero} · {book.nom}</span>
          <span>/</span>
          <span style={{ color: T.navy }}>ÉDITION {book.edition}</span>
        </div>

        {/* Hero */}
        <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", borderRadius: 16, overflow: "hidden", boxShadow: T.shadowLg, marginBottom: 48 }}>
          {/* Left editorial panel */}
          <div style={{ background: "#1A1510", padding: "40px 48px", minHeight: 520, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.gold, letterSpacing: 2, textTransform: "uppercase" }}>RACE BOOK · N° {book.numero}</span>
              <span style={{ fontSize: 10, color: "#9A8F7E", letterSpacing: 1 }}>GRAVV · ÉDITION {book.edition}</span>
            </div>
            <div>
              <div style={{ width: 32, height: 2, background: T.gold, marginBottom: 20 }} />
              <div style={{ fontSize: 11, color: T.gold, letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>
                — {book.tags.slice(0,2).join(" · ")} · {book.lieu?.toUpperCase()}
              </div>
              <div style={{ fontSize: 80, fontWeight: 900, color: "#F2ECD8", lineHeight: 0.9, marginBottom: 8 }}>
                {book.nom}
              </div>
              <div style={{ fontSize: 60, fontWeight: 300, fontStyle: "italic", color: T.gold, lineHeight: 1, marginBottom: 20 }}>
                {book.edition}.
              </div>
              <div style={{ fontSize: 16, color: "#9A8F7E", lineHeight: 1.7, maxWidth: 380 }}>{book.tagline}</div>
            </div>
          </div>
          {/* Right spec sheet */}
          <div style={{ background: T.surface, padding: "28px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.gold, letterSpacing: 2, textTransform: "uppercase" }}>— SPEC SHEET</span>
              <span style={{ fontSize: 10, color: T.textMid }}>{book.nom} · {book.dateISO}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: T.border, borderRadius: 10, overflow: "hidden" }}>
              {[
                { l: "DISTANCE", v: book.distance, u: "km" },
                { l: "D+", v: `${book.dplus.toLocaleString("fr")}`, u: "m" },
                { l: "D-", v: `${book.dminus.toLocaleString("fr")}`, u: "m" },
                { l: "CUTOFF", v: book.cutoff, u: "h" },
                { l: "ITRA", v: `${book.itra}★`, u: "" },
                ...(book.plateau ? [{ l: "PLATEAU", v: book.plateau.toLocaleString("fr"), u: "" }] : []),
                { l: "FINISH RATE", v: `${book.finishRate}`, u: "%" },
                { l: "RAVITOS", v: book.ravitos, u: "" },
              ].map(s => (
                <div key={s.l} style={{ background: T.surface, padding: "10px 14px" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: T.textMid, marginBottom: 3 }}>{s.l}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: T.navy, fontFamily: T.fontData }}>
                    {s.v}<span style={{ fontSize: 11, fontWeight: 400, color: T.textMid }}>{s.u}</span>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: T.gold }}>APERÇU PROFIL</span>
                <span style={{ fontSize: 10, color: T.textMid }}>0 → {book.distance} km</span>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grad-detail" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#C49A3C" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#C49A3C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="km" tick={{ fontSize: 9, fill: T.textMid }} />
                  <YAxis tick={{ fontSize: 9, fill: T.textMid }} />
                  <Tooltip formatter={(v) => [`${v} m`, "Alt."]} labelFormatter={(l) => `km ${l}`} />
                  <Area type="monotone" dataKey="alt" stroke={T.gold} strokeWidth={1.5} fill="url(#grad-detail)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div style={{ marginBottom: 48 }}>
          <div style={{ fontSize: 11, color: T.gold, letterSpacing: 3, textTransform: "uppercase", fontWeight: 700, marginBottom: 12 }}>— SOMMAIRE</div>
          <div style={{ fontSize: 40, fontWeight: 900, color: T.navy, marginBottom: 8 }}>
            Au <span style={{ fontWeight: 300, fontStyle: "italic", color: T.textMid }}>programme</span> de ce numéro.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: `0.5px solid ${T.border}` }}>
            {[
              { n: "01", t: "L'essentiel · synthèse", sub: `${book.insights?.length || 0} points clés`, p: "P. 04" },
              { n: "02", t: "Topographie · profil", sub: `GPX ${book.edition}`, p: "P. 06" },
              { n: "03", t: "Segments clés", sub: `${book.segments?.length || 0} passages annotés`, p: "P. 09" },
              { n: "04", t: "Voix des coureurs", sub: `${getContribs(book).length} contributions`, p: "P. 18" },
            ].map((s, i) => (
              <div key={s.n} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "16px 0", borderBottom: `0.5px solid ${T.border}`, ...(i % 2 === 0 ? { paddingRight: 24, borderRight: `0.5px solid ${T.border}` } : { paddingLeft: 24 }) }}>
                <div style={{ display: "flex", gap: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.gold, fontFamily: T.fontData }}>{s.n}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: T.navy }}>{s.t}</div>
                    <div style={{ fontSize: 11, color: T.textMid, marginTop: 2 }}>{s.sub}</div>
                  </div>
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.gold, fontFamily: T.fontData, whiteSpace: "nowrap" }}>{s.p}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Section 01 — L'essentiel */}
        {book.insights?.length > 0 && (
          <div style={{ marginBottom: 48 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 8 }}>
              <span style={{ fontSize: 56, fontWeight: 700, color: T.gold, fontFamily: T.fontData, lineHeight: 1 }}>01</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.gold, letterSpacing: 3, textTransform: "uppercase" }}>L'ESSENTIEL · SYNTHÈSE COUREURS</span>
            </div>
            <div style={{ fontSize: 36, fontWeight: 900, color: T.navy, marginBottom: 24 }}>
              Ce qu'il faut <span style={{ fontWeight: 300, fontStyle: "italic", color: T.textMid }}>retenir.</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
              {book.insights.map((ins, i) => (
                <div key={i} style={{ background: T.surface, borderRadius: 12, padding: "20px 18px", border: `0.5px solid ${T.border}`, display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 180 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.gold, fontFamily: T.fontData, marginBottom: 10 }}>{ins.numero}</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: T.navy, marginBottom: 10, lineHeight: 1.3 }}>{ins.titre}</div>
                    <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>{ins.texte}</div>
                  </div>
                  <div style={{ fontSize: 11, color: T.textLight, marginTop: 14, paddingTop: 12, borderTop: `0.5px solid ${T.border}` }}>
                    {ins.votes} finishers le mentionnent
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, background: T.border, borderRadius: 10, overflow: "hidden" }}>
              {[
                { l: "MISE À JOUR", v: `Mai 2026 — ${book.version}` },
                { l: "MÉTHODE", v: "Entretiens finishers · ITRA" },
                { l: "ÉDITÉ PAR", v: "GRAVV Editorial" },
                { l: "SOURCES", v: `${book.insights?.length || 0} finishers vérifiés` },
              ].map(m => (
                <div key={m.l} style={{ background: T.surface, padding: "12px 16px" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: T.textMid, marginBottom: 4 }}>{m.l}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{m.v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 02 — Topographie */}
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 8 }}>
            <span style={{ fontSize: 56, fontWeight: 700, color: T.gold, fontFamily: T.fontData, lineHeight: 1 }}>02</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.gold, letterSpacing: 3, textTransform: "uppercase" }}>TOPOGRAPHIE · PROFIL DE COURSE</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 }}>
            <div style={{ fontSize: 36, fontWeight: 900, color: T.navy }}>
              {(book.dplus/1000).toFixed(0)} 000 mètres, <span style={{ fontWeight: 300, fontStyle: "italic", color: T.textMid }}>chronométrés.</span>
            </div>
            <span style={{ fontSize: 11, color: T.textMid }}>P. 06 · GPX v {book.edition}.03</span>
          </div>
          <div style={{ background: T.surface, borderRadius: 16, padding: "24px 28px", boxShadow: T.shadow, border: `0.5px solid ${T.border}` }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: T.navy, marginBottom: 4 }}>
              Profil altimétrique 0 → {book.distance} km
            </div>
            <div style={{ fontSize: 13, color: T.textMid, marginBottom: 16 }}>{book.lieu}</div>
            <div style={{ display: "flex", gap: 16, marginBottom: 16, fontSize: 11, color: T.textMid }}>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: T.green, display: "inline-block" }} />Crew</span>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: T.orange, display: "inline-block" }} />Zone difficile</span>
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: T.gold, display: "inline-block" }} />Ravito</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad-topo" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C49A3C" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#C49A3C" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="km" tick={{ fontSize: 10, fill: T.textMid }} tickFormatter={v => `${v} km`} />
                <YAxis tick={{ fontSize: 10, fill: T.textMid }} tickFormatter={v => `${v} m`} />
                <Tooltip formatter={(v) => [`${v} m`, "Altitude"]} labelFormatter={(l) => `km ${l}`} />
                <Area type="monotone" dataKey="alt" stroke="#1A1510" strokeWidth={2} fill="url(#grad-topo)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Section 03 — Segments */}
        {book.segments?.length > 0 && (
          <div style={{ marginBottom: 48 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 8 }}>
              <span style={{ fontSize: 56, fontWeight: 700, color: T.gold, fontFamily: T.fontData, lineHeight: 1 }}>03</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.gold, letterSpacing: 3, textTransform: "uppercase" }}>SEGMENTS CLÉS · PASSAGES ANNOTÉS</span>
            </div>
            <div style={{ fontSize: 36, fontWeight: 900, color: T.navy, marginBottom: 24 }}>
              {book.segments.length} passages <span style={{ fontWeight: 300, fontStyle: "italic", color: T.textMid }}>stratégiques.</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {book.segments.map((seg, i) => (
                <div key={i} style={{ background: seg.isCritical ? T.goldLight : T.surface, border: `0.5px solid ${seg.isCritical ? T.gold + "60" : T.border}`, borderRadius: 10, padding: "16px 20px", display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: seg.isCritical ? T.gold : T.border, color: seg.isCritical ? "#fff" : T.textMid, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, fontFamily: T.fontData, flexShrink: 0 }}>{i+1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: T.navy }}>{seg.label}</span>
                      {seg.isCritical && <span style={{ fontSize: 10, fontWeight: 700, color: T.orange, letterSpacing: 1, textTransform: "uppercase" }}>CRITIQUE</span>}
                    </div>
                    <div style={{ display: "flex", gap: 12, marginBottom: seg.note ? 8 : 0, flexWrap: "wrap" }}>
                      {seg.distKm && <span style={{ fontSize: 11, color: T.textMid, fontFamily: T.fontData, background: T.bg, padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>{seg.distKm} km</span>}
                      {seg.dplus && <span style={{ fontSize: 11, color: T.green, fontFamily: T.fontData, background: T.greenLight, padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>D+ {seg.dplus}m</span>}
                      {seg.dminus && <span style={{ fontSize: 11, color: T.orange, fontFamily: T.fontData, background: T.orangeLight, padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>D− {seg.dminus}m</span>}
                    </div>
                    {seg.note && <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>{seg.note}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 04 — Voix des coureurs */}
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 8 }}>
            <span style={{ fontSize: 56, fontWeight: 700, color: T.gold, fontFamily: T.fontData, lineHeight: 1 }}>04</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.gold, letterSpacing: 3, textTransform: "uppercase" }}>VOIX DES COUREURS</span>
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, color: T.navy, marginBottom: 24 }}>
            {getContribs(book).length} finishers,{" "}
            <span style={{ fontWeight: 300, fontStyle: "italic", color: T.textMid }}>
              {getContribs(book).length + (book.insights?.length || 0)} contributions.
            </span>
          </div>
          {contribOk && <div style={{ padding: "12px 16px", background: "#5A9E3815", border: `0.5px solid ${T.green}`, borderRadius: 8, color: T.green, fontWeight: 700, fontSize: 13, marginBottom: 16 }}>✓ Merci pour ta contribution !</div>}
          {getContribs(book).length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
              {getContribs(book).map((c, i) => {
                const initials = c.auteur.split(" ").map(w => w[0]).join("").toUpperCase().slice(0,2);
                const avatarColors = ["#C49A3C", "#C85C2A", "#5A9E38", "#2563EB", "#7C3AED", "#1A1510"];
                const avatarBg = avatarColors[i % avatarColors.length];
                return (
                  <div key={c.id} style={{ background: T.surface, borderRadius: 12, padding: "18px 20px", border: `0.5px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: avatarBg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{initials}</div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: T.navy }}>#{c.numero} · {c.auteur}</div>
                        <div style={{ fontSize: 11, color: T.textMid }}>{c.edition}{c.temps ? ` · ${c.temps}` : ""}</div>
                      </div>
                    </div>
                    <span style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", padding: "2px 10px", borderRadius: 20, background: `${BADGE_COLORS[c.badge]}20`, color: BADGE_COLORS[c.badge], width: "fit-content" }}>{c.badge}</span>
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.gold }}>{c.km} </span>
                      <span style={{ fontSize: 13, color: T.navy, lineHeight: 1.6 }}>{c.texte}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textLight, paddingTop: 8, borderTop: `0.5px solid ${T.border}` }}>
                      <span>↑ {c.votes} votes</span>
                      <button style={{ background: "none", border: "none", cursor: "pointer", color: T.textLight, fontFamily: T.fontUI, fontSize: 11 }}>répondre</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ background: T.surfaceSub, borderRadius: 12, padding: "32px 24px", textAlign: "center", border: `0.5px dashed ${T.border}`, marginBottom: 24 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✍️</div>
              <div style={{ fontWeight: 600, fontSize: 14, color: T.navy }}>Sois le premier à contribuer</div>
              <div style={{ fontSize: 13, color: T.textMid, marginTop: 6 }}>Partage ton expérience sur cette course</div>
            </div>
          )}

          {/* Contribution form toggle */}
          <button onClick={() => setShowContribForm(f => !f)} style={{
            background: "transparent", border: `1px solid ${T.gold}`, borderRadius: 8,
            color: T.gold, fontFamily: T.fontUI, fontSize: 13, fontWeight: 700,
            padding: "10px 20px", cursor: "pointer",
          }}>✍️ Ajouter un retour d'expérience</button>

          {showContribForm && (
            <div style={{ marginTop: 16, background: T.surface, borderRadius: 12, padding: "20px 24px", border: `0.5px solid ${T.border}` }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: T.navy, marginBottom: 16 }}>Ton retour d'expérience</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                {["TIP", "WARNING", "NUTRI"].map(b => (
                  <button key={b} onClick={() => setContribForm(f => ({ ...f, badge: b }))} style={{
                    padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer",
                    background: contribForm.badge === b ? BADGE_COLORS[b] : "transparent",
                    color: contribForm.badge === b ? "#fff" : BADGE_COLORS[b],
                    border: `1px solid ${BADGE_COLORS[b]}`,
                  }}>{b}</button>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.textMid, display: "block", marginBottom: 4 }}>KM / ZONE</label>
                  <input value={contribForm.km} onChange={e => setContribForm(f => ({ ...f, km: e.target.value }))} placeholder="ex: km 80 ou km 50-80" style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `0.5px solid ${T.border}`, fontFamily: T.fontUI, fontSize: 13, boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.textMid, display: "block", marginBottom: 4 }}>ÉDITION / RÉSULTAT</label>
                  <input value={contribForm.edition} onChange={e => setContribForm(f => ({ ...f, edition: e.target.value }))} placeholder="ex: UTMB 2024 · 32h14" style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: `0.5px solid ${T.border}`, fontFamily: T.fontUI, fontSize: 13, boxSizing: "border-box" }} />
                </div>
              </div>
              <textarea value={contribForm.texte} onChange={e => setContribForm(f => ({ ...f, texte: e.target.value }))} placeholder="Ton conseil, avertissement ou note de nutrition..." rows={4} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `0.5px solid ${T.border}`, fontFamily: T.fontUI, fontSize: 13, resize: "vertical", boxSizing: "border-box", marginBottom: 12 }} />
              <button onClick={async () => {
                if (!session) { alert("Connectez-vous pour contribuer"); return; }
                if (!contribForm.texte.trim()) { alert("Le texte est requis"); return; }
                setContribSubmitting(true);
                const row = {
                  race_id: selectedId,
                  auteur: profile?.prenom ? `${profile.prenom} ${(profile.nom || "").charAt(0)}.` : "Anonyme",
                  badge:   contribForm.badge,
                  km:      contribForm.km,
                  edition: contribForm.edition,
                  texte:   contribForm.texte,
                  votes:   0,
                };
                const saved = db ? await db.addContribution(session.user.id, row) : null;
                setContribSubmitting(false);
                if (saved) {
                  setLiveContribs(prev => ({
                    ...prev,
                    [selectedId]: [saved, ...(prev[selectedId] || [])],
                  }));
                }
                setContribOk(true);
                setContribForm({ badge: "TIP", km: "", texte: "", edition: "" });
                setShowContribForm(false);
                setTimeout(() => setContribOk(false), 4000);
              }} disabled={contribSubmitting} style={{ background: T.navy, color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 13, fontWeight: 700, cursor: contribSubmitting ? "not-allowed" : "pointer", fontFamily: T.fontUI, opacity: contribSubmitting ? 0.6 : 1 }}>
                {contribSubmitting ? "Envoi…" : "Envoyer ma contribution"}
              </button>
            </div>
          )}
        </div>

        {/* Dark CTA section */}
        <div style={{ background: "#1A1510", borderRadius: 16, padding: "48px 44px", marginBottom: 40 }}>
          <div style={{ fontSize: 11, color: T.gold, letterSpacing: 3, textTransform: "uppercase", fontWeight: 700, marginBottom: 16 }}>— SI TU L'AJOUTES À TA SAISON</div>
          <div style={{ fontSize: 36, fontWeight: 900, color: "#F2ECD8", lineHeight: 1.2, marginBottom: 32, maxWidth: 600 }}>
            Tu reçois un dossier de course <span style={{ color: T.gold }}>complet</span>, calé sur ton profil.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: "#2A2015", borderRadius: 12, overflow: "hidden" }}>
            {[
              { icon: "📡", t: "Trace GPX + profil", sub: `Officielle ${book.edition}, ${book.segments?.length || 0} segments analysés au modèle Minetti.` },
              { icon: "⏱️", t: "Plan d'allure perso", sub: "Pacing par segment calé sur ta VMA et ton historique." },
              { icon: "🥤", t: "Plan nutrition", sub: "70 g/h glucides · 600 mg/h sodium synchronisés aux ravitos." },
              { icon: "👥", t: "Crew & drop bags", sub: "Timeline crew, lien partagé, drop bags pré-remplis." },
              { icon: "💬", t: "Voix des coureurs", sub: `${getContribs(book).length + (book.insights?.length || 0)} contributions classées par km, mises à jour à chaque édition.` },
              { icon: "📅", t: "Plan d'entraînement", sub: "22 semaines spécifiques générées depuis ton volume actuel." },
            ].map(f => (
              <div key={f.t} style={{ background: "#1A1510", padding: "20px 24px", borderTop: "0.5px solid #2A2015" }}>
                <div style={{ width: 36, height: 36, background: `${T.gold}20`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, marginBottom: 12 }}>{f.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#F2ECD8", marginBottom: 6 }}>{f.t}</div>
                <div style={{ fontSize: 12, color: "#9A8F7E", lineHeight: 1.5 }}>{f.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Sticky bottom bar */}
        {!stickyDismissed && (
          <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100, background: "#1A1510", padding: "14px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 -4px 20px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.gold, color: "#1A1510", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, fontFamily: T.fontData, flexShrink: 0 }}>{book.numero}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#F2ECD8" }}>
                  {book.nom} <span style={{ fontStyle: "italic", color: T.gold }}>te tente ?</span>
                </div>
                <div style={{ fontSize: 11, color: "#9A8F7E" }}>Une seconde pour tout récupérer : trace, voix coureurs, plan personnalisé.</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button onClick={() => {
                if (!session && !addRace) { alert("Connectez-vous pour ajouter à votre planning"); return; }
                if (addRace) {
                  addRace({
                    nom: book.nom, date: book.dateISO, distance: book.distance,
                    lieu: book.lieu, statut: "inscrit", isTrail: true,
                    courseProfile: { surface: "trail", profil: "montagne", denivele_plus: book.dplus, denivele_minus: book.dminus, altitude: 0, temperature: 15, vent: "nul", orientation_vent: "variable", humidite: "normale", virages: "larges", revetement: "terre", tags: book.tags, note: book.sousTitre },
                    gpxData: { fileName: `${book.id}-${book.edition}.gpx`, distance: book.distance, elevGain: book.dplus, elevLoss: book.dminus, profile: book.profile.map(([km,alt]) => [km, alt]) },
                    splits: [], nutrition: [], tapering: [],
                  });
                  setStickyDismissed(true);
                  alert(`${book.nom} ajouté à ton planning !`);
                }
              }} style={{ background: T.gold, color: "#1A1510", border: "none", borderRadius: 40, padding: "10px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: T.fontUI, whiteSpace: "nowrap" }}>
                + Ajouter à ma saison
              </button>
              <button onClick={() => setStickyDismissed(true)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9A8F7E", fontSize: 18, padding: 4 }}>×</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
